import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React from 'react';
import {BaseGlowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-glow-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {convertFabricShadowObjectToShadowOrGlowOptionsObject, getDefaultFabricShadowForType, getFabricShadowOrGlowTypeFromGlowType, isGlow} from '@Utils/fabric-shadow.util';
import {GlowType} from '@PosterWhiteboard/poster/poster-backend.types';
import {ShadowOrGlowType} from '@postermywall/fabricjs-2';
import type {RGBA} from '@Utils/color.util';

interface ItemGlowOptionProps {
  onClickColorFill(): void;
}

export function ItemGlowOption({...props}: ItemGlowOptionProps): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const shadow = useGetKeyFromActiveItem('shadow');

  if (!activeItem) {
    throw new Error('Active item does not exist in ItemGlowOption');
  }

  if (shadow === undefined) {
    throw new Error('item does not have shadow property');
  }

  const glowOptions = useMemo(() => {
    return convertFabricShadowObjectToShadowOrGlowOptionsObject(shadow, activeItem.getScaleForShadow());
  }, [shadow, activeItem]);

  const getSelectedGlowType = (): GlowType => {
    if (!glowOptions) {
      return GlowType.NONE;
    }

    switch (glowOptions.shadowOrGlowType) {
      case ShadowOrGlowType.LIGHT_GLOW:
        return GlowType.LIGHT;
      case ShadowOrGlowType.STRONG_GLOW:
        return GlowType.STRONG;
      case ShadowOrGlowType.CUSTOM_SHADOW:
      case ShadowOrGlowType.STRONG_SHADOW:
      case ShadowOrGlowType.LIGHT_SHADOW:
      case null:
        return GlowType.NONE;
      default:
        throw new Error(`Unhandled shadowOrGlowType type: ${glowOptions.shadowOrGlowType} in ItemGlowOption`);
    }
  };

  const getGlowColor = (): RGBA => {
    if (!glowOptions) {
      return [0, 0, 0, 0];
    }

    return glowOptions.color;
  };

  const onClickHandler = (glowType: GlowType): void => {
    const newType = getFabricShadowOrGlowTypeFromGlowType(glowType);
    const currentType = shadow?.shadowOrGlowType;

    const updatedShadow = getDefaultFabricShadowForType(newType, activeItem.gitype, activeItem.getScaleForShadow());

    if (!updatedShadow) {
      void activeItem.updateFromObject({shadow: null});

      return;
    }

    const shouldPreserveColor = isGlow(currentType) && isGlow(newType);

    const {color, ...restShadow} = updatedShadow;

    const shadowToApply = shouldPreserveColor ? {...restShadow} : updatedShadow;

    void activeItem.updateFromObject({shadow: shadowToApply});
  };

  return (
    <BaseGlowOption
      glowType={getSelectedGlowType()}
      glowColor={getGlowColor()}
      onApplyGlow={onClickHandler}
      onClickColorFill={(): void => {
        props.onClickColorFill();
      }}
    />
  );
}
